import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styles from './PreloadedLandingV2.module.css';
import { logEvent } from '../../analytics';
import { saveStateBeforeNavigation } from '../../store/reducers/progressionReducer';

const SECTION_KEYS = ['hero', 's1', 's2', 's3', 's4', 's5', 's6', 's7'];

// Les hexagones de /public/lp2/icons/ sont decoratifs : le libelle qui les suit
// porte deja l'information, ils restent donc hors de l'arbre d'accessibilite.
// « New Delivery System » n'a pas d'icone dans le jeu livre par le client.
const WHY_IT_MATTERS = [
  { label: 'Swiss Made', icon: 'swiss-made.png' },
  { label: 'Preloaded', icon: 'preloaded.png' },
  { label: 'Optimized for low light conditions', icon: 'low-light.png' },
  { label: 'True-to-life vision', icon: 'true-to-life.png' },
  // « STAR » ressort en gras, comme sur l'hexagone livre par le client.
  { label: 'EDOF STAR technology', icon: 'edof-star.png', strong: 'STAR' }
];

// Portail d'entree SAV-IOL. Le fragment #event ouvre directement le formulaire
// d'inscription ; sans lui le visiteur atterrit sur le choix des trois parcours.
const EVENT_FORM_URL = 'https://join.sav-iol.com/#event';

// Les quatre moments partagent le meme cadrage : seule la lumiere change.
const DAY_NIGHT_STOPS = [
  { id: 'day', label: 'Day', className: 'layerDay', image: 'day.webp' },
  { id: 'golden', label: 'Golden hour', className: 'layerGolden', image: 'golden-hour.webp' },
  { id: 'dusk', label: 'Dusk', className: 'layerDusk', image: 'dusk.webp' },
  { id: 'night', label: 'Night', className: 'layerNight', image: 'night.webp' }
];

// Les visuels de /public/lp2/ sont passes en variables CSS : le degrade de repli
// declare dans la feuille de style prend le relais tant qu'un fichier manque.
// Chaque scene existe en deux cadrages, le 16/9 et le 9/16 suffixe -portrait ;
// c'est la feuille de style qui choisit selon l'orientation du viewport.
const mediaImage = (filename) => ({
  '--lp2-landscape': `url('/lp2/${filename}')`,
  '--lp2-portrait': `url('/lp2/${filename.replace(/(\.\w+)$/, '-portrait$1')}')`
});

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Progression d'une valeur sur un intervalle, ramenee entre 0 et 1.
const range = (value, start, end) => clamp((value - start) / (end - start), 0, 1);

// Apparition puis disparition d'un bloc de texte au fil de sa section.
const reveal = (p) => range(p, 0.12, 0.38) * (1 - range(p, 0.72, 0.98));

const easeOut = (p) => 1 - Math.pow(1 - p, 3);

// Depart et arrivee adoucis, pour une montee sans a-coup.
const smoothstep = (p) => p * p * (3 - 2 * p);

// Section 3, le teaser. Faute de modele 3D, la scene est composee a plat : deux
// visuels produit traversent le champ en sens inverse, plus larges que l'ecran
// pour qu'on n'en voie jamais l'ensemble. Chacun porte un reflet qui glisse sur
// sa surface, decoupe par sa propre silhouette (voir --piece dans la CSS).
// Les positions sont en pourcentage de la largeur de la piece.
const TEASER_PIECES = [
  {
    id: 'injector',
    className: 'teaserInjector',
    image: 'injector.webp',
    // Entre par la droite, sort par la gauche en piquant legerement du nez.
    from: { x: 52, y: -14, rotate: -18 },
    to: { x: -50, y: 6, rotate: -8 },
    // Se devoile en premier, avant que la lentille n'entre dans le champ.
    window: [0.04, 0.5],
  },
  {
    id: 'lens',
    className: 'teaserLens',
    image: 'lens.webp',
    // Arrive de la gauche, comme dans la version 3D. Amplitude volontairement
    // reduite : c'est le reflet, anime en continu, qui porte l'effet.
    from: { x: -58, y: 18, rotate: 11 },
    to: { x: 34, y: -6, rotate: -3 },
    window: [0.24, 0.72]
  }
];

export default function PreloadedLandingV2() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [progress, setProgress] = useState(() =>
    SECTION_KEYS.reduce((acc, key) => ({ ...acc, [key]: 0 }), {})
  );
  const [dayNight, setDayNight] = useState(0);

  const sectionRefs = useRef(
    SECTION_KEYS.reduce((acc, key) => ({ ...acc, [key]: React.createRef() }), {})
  ).current;

  useEffect(() => {
    logEvent('page_view', 'preloaded_landing_v2');
    dispatch(saveStateBeforeNavigation());

    return () => {
      document.body.style.overflow = '';
      window.scrollTo(0, 0);
    };
  }, [dispatch]);

  useEffect(() => {
    let frame = null;

    const measure = () => {
      frame = null;
      const viewportHeight = window.innerHeight;
      const next = {};

      SECTION_KEYS.forEach((key) => {
        const element = sectionRefs[key].current;
        if (!element) {
          next[key] = 0;
          return;
        }
        const rect = element.getBoundingClientRect();
        const travel = viewportHeight + rect.height;
        // 0 quand la section entre par le bas, 1 quand elle sort par le haut.
        const raw = clamp((viewportHeight - rect.top) / travel, 0, 1);
        next[key] = Math.round(raw * 200) / 200;
      });

      setProgress((current) =>
        SECTION_KEYS.every((key) => current[key] === next[key]) ? current : next
      );
    };

    const onScroll = () => {
      if (frame === null) {
        frame = requestAnimationFrame(measure);
      }
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [sectionRefs]);

  const handleBackToHome = useCallback(() => {
    logEvent('ui_interaction', 'back_to_home_click');
    document.body.style.overflow = '';
    window.scrollTo(0, 0);
    sessionStorage.setItem('saviol-returning-from-new-product', 'true');
    navigate('/');
  }, [navigate]);

  const handleMeetUs = useCallback(() => {
    logEvent('ui_interaction', 'preloaded_v2_meet_us_escrs');
  }, []);

  const activeStopIndex = Math.round(dayNight * (DAY_NIGHT_STOPS.length - 1));

  // Montee tres lente hors du noir, puis extinction avant la section 4.
  const teaserEmerge =
    smoothstep(range(progress.s3, 0.06, 0.5)) * (1 - smoothstep(range(progress.s3, 0.8, 1)));

  return (
    <div className={styles.container}>
      {/* La scene reste en arriere-plan : seule la section 3, transparente, la laisse voir. */}
      <div
        className={styles.canvasContainer}
        style={{ opacity: range(progress.s3, 0.01, 0.06) * (1 - range(progress.s3, 0.92, 1)) }}
      >
        <div className={styles.teaserScene}>
          <div className={styles.teaserGlow} style={{ opacity: 0.25 + teaserEmerge * 0.75 }} />

          {TEASER_PIECES.map((piece) => {
            // Chaque piece a sa propre fenetre : elles ne traversent pas ensemble.
            const local = range(progress.s3, piece.window[0], piece.window[1]);
            const travel = smoothstep(local);
            const at = (key) => piece.from[key] + (piece.to[key] - piece.from[key]) * travel;
            // Le reflet suit le scroll et non une horloge. Il reste lineaire la
            // ou la piece est lissee, ce qui evite qu'il colle a son deplacement.
            // 355% : la course exacte pour traverser la piece de bord a bord.
            const sweep = local * 355;

            return (
              <div
                key={piece.id}
                className={`${styles.teaserPiece} ${styles[piece.className]}`}
                style={{
                  '--piece': `url('/lp2/${piece.image}')`,
                  '--emerge': teaserEmerge,
                  '--sweep': `${sweep}%`,
                  transform: `translate3d(${at('x')}%, ${at('y')}%, 0) rotate(${at('rotate')}deg)`
                }}
              >
                <span className={styles.teaserSheen} aria-hidden="true" />
              </div>
            );
          })}
        </div>
      </div>

      <button className={styles.backButton} onClick={handleBackToHome}>
        ← Back to Swiss Virtual Clinic
      </button>

      <div className={styles.content}>
        {/* ========== HERO — WELCOME ABOARD ========== */}
        <section ref={sectionRefs.hero} className={styles.hero}>
          <div
            className={`${styles.mediaLayer} ${styles.heroMedia}`}
            style={{ ...mediaImage('cabin-hero.webp'), transform: `scale(${1 + progress.hero * 0.08})` }}
          />
          <div className={styles.heroScrim} />
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>Welcome aboard.</h1>
            <span className={styles.accentRule} aria-hidden="true" />
            <p className={styles.heroSubtitle}>
              You are among the first surgeons invited to discover what is coming next.
            </p>
            <div className={styles.scrollCue}>
              <span className={styles.scrollArrow} aria-hidden="true">↓</span>
              <span>Scroll to begin your journey.</span>
            </div>
          </div>
        </section>

        {/* ========== SECTION 1 — THE CHALLENGE ========== */}
        <section ref={sectionRefs.s1} className={styles.storySection}>
          <div
            className={`${styles.mediaLayer} ${styles.challengeMedia}`}
            style={{
              ...mediaImage('landscape-challenge.webp'),
              // La telecabine avance legerement au scroll.
              transform: `scale(1.12) translate3d(${progress.s1 * 3}%, ${progress.s1 * -2.5}%, 0)`
            }}
          />
          <div className={styles.storyScrim} />
          <div className={styles.storyContent} style={{ opacity: reveal(progress.s1) }}>
            <span className={styles.eyebrow}>The Challenge</span>
            <span className={styles.accentRule} aria-hidden="true" />
            <p className={styles.storyText}>
              Low-light vision remains one of the greatest unmet expectations in premium
              cataract surgery.
            </p>
          </div>
        </section>

        {/* ========== SECTION 2 — THE OPPORTUNITY ========== */}
        <section ref={sectionRefs.s2} className={styles.storySection}>
          <div
            className={`${styles.mediaLayer} ${styles.opportunityMedia}`}
            style={{
              ...mediaImage('landscape-opportunity.webp'),
              transform: `scale(1.12) translate3d(${progress.s2 * 2}%, ${progress.s2 * -2}%, 0)`
            }}
          />
          {/* La lumiere devient legerement plus chaude. */}
          <div
            className={styles.warmTint}
            style={{ opacity: 0.15 + easeOut(progress.s2) * 0.45 }}
          />
          <div className={styles.storyScrim} />
          <div className={styles.storyContent} style={{ opacity: reveal(progress.s2) }}>
            <span className={styles.eyebrow}>The Opportunity</span>
            <span className={styles.accentRule} aria-hidden="true" />
            <p className={styles.storyText}>
              What if continuous vision and low-light performance no longer required
              compromise?
            </p>
          </div>
        </section>

        {/* ========== SECTION 3 — SOMETHING IS TAKING SHAPE ========== */}
        <section ref={sectionRefs.s3} className={styles.shapeSection}>
          <div className={styles.shapeVignette} />
          <div
            className={styles.shapeContent}
            style={{ opacity: range(progress.s3, 0.45, 0.65) * (1 - range(progress.s3, 0.86, 0.98)) }}
          >
            <h2 className={styles.shapeTitle}>Something is taking shape.</h2>
          </div>
        </section>

        {/* ========== SECTION 4 — SWISS INNOVATION ========== */}
        {/* Pas de photo ici : la section fait respirer le parcours entre deux
            visuels, sur le seul degrade Pantone. */}
        <section ref={sectionRefs.s4} className={`${styles.storySection} ${styles.summitSection}`}>
          <div className={styles.storyContent} style={{ opacity: reveal(progress.s4) }}>
            <span className={styles.eyebrow}>Swiss Innovation</span>
            <span className={styles.accentRule} aria-hidden="true" />
            <p className={styles.storyText}>
              Engineered in Switzerland.
              <br />
              Designed for surgical certainty.
            </p>
          </div>
        </section>

        {/* ========== SECTION 5 — DAY / NIGHT ========== */}
        <section ref={sectionRefs.s5} className={styles.experienceSection}>
          <div className={styles.experienceStage}>
            {DAY_NIGHT_STOPS.map((stop, index) => (
              <div
                key={stop.id}
                className={`${styles.mediaLayer} ${styles[stop.className]}`}
                style={{
                  ...mediaImage(stop.image),
                  // Le premier calque reste opaque : les suivants se superposent.
                  opacity:
                    index === 0
                      ? 1
                      : range(dayNight, (index - 1) / (DAY_NIGHT_STOPS.length - 1), index / (DAY_NIGHT_STOPS.length - 1))
                }}
              />
            ))}
            <div className={styles.experienceScrim} />
          </div>

          <div className={styles.sliderPanel}>
            {/* Day et Night encadrent la piste sur une seule ligne. */}
            <div className={styles.sliderRow}>
              <span
                className={`${styles.sliderLabel} ${dayNight < 0.5 ? styles.sliderLabelActive : ''}`}
              >
                Day
              </span>
              <input
                className={styles.slider}
                type="range"
                min="0"
                max="1"
                step="0.001"
                value={dayNight}
                onChange={(event) => setDayNight(Number(event.target.value))}
                aria-label="Move from day to night"
              />
              <span
                className={`${styles.sliderLabel} ${dayNight >= 0.5 ? styles.sliderLabelActive : ''}`}
              >
                Night
              </span>
            </div>
            <p className={styles.sliderMoment}>{DAY_NIGHT_STOPS[activeStopIndex].label}</p>
          </div>
        </section>

        {/* ========== SECTION 6 — WHY IT MATTERS ========== */}
        <section ref={sectionRefs.s6} className={styles.mattersSection}>
          <div className={styles.mattersInner}>
            <h2 className={styles.mattersTitle}>Why it matters</h2>
            <span className={`${styles.accentRule} ${styles.accentRuleLeft}`} aria-hidden="true" />
            <ul className={styles.mattersList}>
              {WHY_IT_MATTERS.map((item) => {
                const [before, after] = item.strong
                  ? item.label.split(item.strong)
                  : [item.label, ''];

                return (
                  <li key={item.label} className={styles.mattersItem}>
                    {item.icon ? (
                      <img className={styles.mattersIcon} src={`/lp2/icons/${item.icon}`} alt="" />
                    ) : (
                      // Cale vide : sans elle, le libelle sans icone romprait l'alignement.
                      <span className={styles.mattersIcon} aria-hidden="true" />
                    )}
                    {/* Un seul enfant flex pour le libelle : sinon le gap de la
                        ligne s'inserait aussi entre les fragments de texte. */}
                    <span>
                      {before}
                      {item.strong && <span className={styles.mattersStrong}>{item.strong}</span>}
                      {after}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* ========== SECTION 7 — SHOW ME THE SCIENCE ========== */}
        <section ref={sectionRefs.s7} className={styles.scienceSection}>
          <div
            className={`${styles.mediaLayer} ${styles.lighthouseMedia}`}
          />
          <div className={styles.lighthouseBeam} aria-hidden="true" />
          <div className={styles.scienceScrim} />
          <div className={styles.scienceContent}>
            <h2 className={styles.scienceTitle}>Show me the science</h2>
            <span className={styles.accentRule} aria-hidden="true" />
            <p className={styles.scienceText}>
              Discover the first clinical evidence exclusively during
            </p>
            <p className={styles.scienceEvent}>Aboard Swiss Vision</p>
            <p className={styles.scienceDate}>11 September</p>
            <p className={styles.scienceNote}>Upon invitation.</p>
            <a
              className={styles.scienceCta}
              href={EVENT_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleMeetUs}
            >
              Meet us at ESCRS
            </a>
          </div>
        </section>

        <footer className={styles.footer}>
          <p>© 2026 SAV-IOL - Swiss Advanced Vision</p>
          <p>
            <a href="mailto:info@sav-iol.com">info@sav-iol.com</a> |
            <a href="tel:+41325665400"> +41 32 566 54 00</a>
          </p>
        </footer>
      </div>
    </div>
  );
}
